'use client'

import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva } from 'class-variance-authority'
import type { TextProps } from './text.types'
import { cn } from '../lib/utils'

/**
 * Single source of truth for the text type scale + tones. `size` maps semantic
 * roles onto the DS typography tokens (see the Typography story), so we never
 * hand-write raw `text-sm`/`text-xs`. Use `size`/`tone` everywhere for consistency.
 */
const textVariants = cva('', {
  variants: {
    size: {
      title: 'text-header-small', // section / panel headings
      body: 'text-body-medium', // default paragraph text
      caption: 'text-body-small', // small body / captions
      label: 'text-label-medium font-medium', // form labels, control text
      hint: 'text-label-small', // secondary hints, metadata
    },
    tone: {
      default: 'text-on-surface',
      muted: 'text-on-surface-secondary',
    },
  },
})


function Text({ inline, size, tone, className, render, ...props }: TextProps) {
  const defaultProps = {
    className: cn(
      // only emit scale classes when a size/tone is requested, so bare <Text> stays unstyled
      (size !== undefined || tone !== undefined) &&
        textVariants({ size, tone }),
      inline && 'inline',
      className,
    ),
    'data-slot': 'text',
  }

  return useRender({
    defaultTagName: 'p',
    props: mergeProps<'p'>(defaultProps, props),
    render,
  })
}

export { Text, textVariants }
